import React, { useEffect, useMemo, useRef } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { strapiListInvoiceGridRows } from '../../api/strapiCrm';
import { appDocumentTitle } from '../../appMeta';
import { useAuth } from '../../auth/AuthContext';
import { useAppUi } from '../../context/AppUiContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { PageShell } from '../../layout/PageShell';
import { useNavigationMenuQuery } from '../../query/hooks';
import { queryKeys } from '../../query/queryKeys';
import { LhuCustomHtmlDialog } from './components/LhuCustomHtmlDialog';

function decodeJobNumber(raw: string | undefined): string {
  if (!raw) return '';
  try {
    return decodeURIComponent(raw).trim();
  } catch {
    return raw.trim();
  }
}

export function LhuContentEditRoute() {
  const { jwt } = useAuth();
  const navigate = useNavigate();
  const { jobNumber: rawJobNumber } = useParams<{ jobNumber: string }>();
  const jobNumber = decodeJobNumber(rawJobNumber);
  const menuQ = useNavigationMenuQuery(jwt);
  const canView = menuQ.data?.access_codes?.includes('can_view_hasil_uji_letter') ?? false;
  const { navDrawerOpen, setNavDrawerOpen } = useAppUi();
  const navWasOpen = useRef(navDrawerOpen);

  useDocumentTitle(appDocumentTitle(jobNumber ? `Edit LHU ${jobNumber}` : 'Edit LHU'));

  useEffect(() => {
    const previous = navWasOpen.current;
    setNavDrawerOpen(false);
    return () => setNavDrawerOpen(previous);
  }, [setNavDrawerOpen]);

  const rowsQ = useQuery({
    queryKey: queryKeys.invoiceGridRows(jwt),
    queryFn: () => strapiListInvoiceGridRows(jwt!),
    enabled: Boolean(jwt && canView),
  });

  const row = useMemo(
    () => rowsQ.data?.find((item) => item.job_number.trim() === jobNumber) ?? null,
    [rowsQ.data, jobNumber],
  );

  if (menuQ.isPending) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (menuQ.isError) {
    return (
      <Alert severity="error">
        {menuQ.error instanceof Error ? menuQ.error.message : 'Gagal memuat menu permission.'}
      </Alert>
    );
  }

  if (!canView) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <PageShell
      title={jobNumber ? `Edit konten LHU — ${jobNumber}` : 'Edit konten LHU'}
      subtitle={row?.quotation_number || undefined}
      fill
      testId="lhu-content-edit-page"
    >
      {!jobNumber ? (
        <Alert severity="warning">Job number tidak ada di URL.</Alert>
      ) : rowsQ.isPending ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : rowsQ.isError ? (
        <Alert severity="error">
          {rowsQ.error instanceof Error ? rowsQ.error.message : 'Gagal memuat data work order.'}
        </Alert>
      ) : !row ? (
        <Alert severity="warning">Work order dengan job number {jobNumber} tidak ditemukan.</Alert>
      ) : (
        <LhuCustomHtmlDialog
          open
          workOrderDocumentId={row.work_order_document_id}
          jwt={jwt}
          onClose={() => navigate('/sampling/hasil-uji-letter')}
        />
      )}
    </PageShell>
  );
}
